import { createFileRoute, Link, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { CampaignDetailDrawer } from '@/components/admin/CampaignDetailDrawer';
import { apiJson } from '@/lib/api';
import type {
  AdminDripCatalogEntry,
  AdminDripCatalogResponse,
  AdminDripScheduleRow,
  AdminDripSummaryResponse,
} from '@/lib/types';

const CONV_PER_SEND_TOOLTIP =
  'Conversions ÷ sends in the selected range. Directional yield for comparing campaigns; not a strict cohort rate.';

function AdminDripOverviewPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useSearch({ from: '/admin/drip-campaigns' });
  const range = (search.range ?? '30d') as '7d' | '30d' | '90d' | 'all';
  const campaignId = search.campaign;

  const catalogQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'catalog'],
    queryFn: () => apiJson<AdminDripCatalogResponse>(getToken, '/v1/admin/drip-campaigns/catalog'),
  });

  const summaryQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'summary', range],
    queryFn: () =>
      apiJson<AdminDripSummaryResponse>(getToken, `/v1/admin/drip-campaigns/summary?range=${range}`),
  });

  const schedulesQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'schedules'],
    queryFn: () => apiJson<AdminDripScheduleRow[]>(getToken, '/v1/admin/drip-campaigns/schedules'),
  });

  const summaryById = new Map(summaryQuery.data?.per_campaign.map((r) => [r.id, r]) ?? []);
  const catalogById = new Map<string, AdminDripCatalogEntry>(
    (catalogQuery.data?.campaigns ?? []).map((c) => [c.id, c])
  );
  const drawerEntry = campaignId ? catalogById.get(campaignId) : undefined;

  const totals = summaryQuery.data?.totals;

  const setRange = (r: '7d' | '30d' | '90d' | 'all') => {
    navigate({ to: pathname, search: { ...search, range: r } });
  };

  const goalHref = (g: AdminDripCatalogEntry['goal']) => `/admin/drip-campaigns/${g}`;

  return (
    <div className='space-y-8'>
      <div className='flex flex-wrap items-center gap-3'>
        <label className='text-xs font-semibold text-gray-600'>Range</label>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value as '7d' | '30d' | '90d' | 'all')}
          className='px-3 py-2 border border-gray-300 rounded-md text-sm'
        >
          <option value='7d'>7 days</option>
          <option value='30d'>30 days</option>
          <option value='90d'>90 days</option>
          <option value='all'>All time</option>
        </select>
        <span
          className='text-xs text-gray-500 cursor-help'
          title='Clicks are not persisted in the database; only first-open is stored when tracking is enabled.'
        >
          ℹ Opens / clicks
        </span>
      </div>

      {summaryQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full' />
        </div>
      ) : summaryQuery.isError ? (
        <p className='text-red-600'>Could not load summary.</p>
      ) : totals ? (
        <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2'>
          {(
            [
              ['Total sends', totals.sends],
              ['Opens (tracked campaigns)', totals.opens],
              ['Conversions (proxy)', totals.conversions],
            ] as const
          ).map(([label, val]) => (
            <div key={label} className='bg-white rounded-lg border border-gray-200 px-3 py-3 shadow-sm'>
              <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight'>
                {label}
              </p>
              <p className='mt-1 text-lg font-semibold tabular-nums text-gray-900'>{val}</p>
            </div>
          ))}
          <div
            className='bg-white rounded-lg border border-gray-200 px-3 py-3 shadow-sm cursor-help'
            title={CONV_PER_SEND_TOOLTIP}
          >
            <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight'>
              Conv. % (blended)
            </p>
            <p className='mt-1 text-lg font-semibold tabular-nums text-gray-900'>
              {totals.conversion_rate != null && totals.sends > 0
                ? `${(100 * totals.conversion_rate).toFixed(1)}%`
                : '—'}
            </p>
          </div>
        </div>
      ) : null}

      <div>
        <h2 className='text-lg font-semibold text-gray-900 mb-3'>All campaigns</h2>
        {catalogQuery.isLoading ? (
          <p className='text-gray-500 text-sm'>Loading catalog…</p>
        ) : catalogQuery.isError ? (
          <p className='text-red-600 text-sm'>Could not load catalog.</p>
        ) : (
          <div className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
            <table className='w-full text-sm'>
              <thead className='bg-gray-50 text-left text-xs uppercase text-gray-500'>
                <tr>
                  <th className='px-4 py-2'>Campaign</th>
                  <th className='px-4 py-2'>Goal</th>
                  <th className='px-4 py-2 text-right'>Sends</th>
                  <th className='px-4 py-2 text-right'>Open %</th>
                  <th className='px-4 py-2 text-right'>Eligible</th>
                  <th className='px-4 py-2 text-right'>Conv.</th>
                  <th
                    className='px-4 py-2 text-right cursor-help'
                    title={CONV_PER_SEND_TOOLTIP}
                  >
                    Conv. %
                  </th>
                  <th className='px-4 py-2' />
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {(catalogQuery.data?.campaigns ?? []).map((c) => {
                  const s = summaryById.get(c.id);
                  const openPct =
                    c.tracking_enabled && s?.open_rate != null && s.sends > 0
                      ? `${(100 * s.open_rate).toFixed(1)}%`
                      : '—';
                  const convPct =
                    s && s.sends > 0 && s.conversion_rate != null
                      ? `${(100 * s.conversion_rate).toFixed(1)}%`
                      : '—';
                  return (
                    <tr key={c.id} className='hover:bg-gray-50'>
                      <td className='px-4 py-2'>
                        <span className='font-medium text-gray-900'>{c.name}</span>
                        <p className='text-[10px] font-mono text-gray-400'>{c.id}</p>
                        {!c.tracking_enabled && (
                          <span className='mt-0.5 inline-block text-[9px] font-semibold uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded'>
                            tracking off
                          </span>
                        )}
                      </td>
                      <td className='px-4 py-2 capitalize'>
                        <Link to={goalHref(c.goal)} search={search} className='text-primary hover:underline'>
                          {c.goal}
                        </Link>
                      </td>
                      <td className='px-4 py-2 text-right tabular-nums'>{s?.sends ?? 0}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{openPct}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>
                        {s?.eligible_now == null ? '—' : s.eligible_now}
                      </td>
                      <td className='px-4 py-2 text-right tabular-nums'>{s?.conversions ?? 0}</td>
                      <td
                        className='px-4 py-2 text-right tabular-nums text-gray-700'
                        title={CONV_PER_SEND_TOOLTIP}
                      >
                        {convPct}
                      </td>
                      <td className='px-4 py-2'>
                        <button
                          type='button'
                          onClick={() =>
                            navigate({ to: pathname, search: { ...search, campaign: c.id } })
                          }
                          className='text-primary text-sm font-medium hover:underline'
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className='text-lg font-semibold text-gray-900 mb-3'>Celery Beat (schedules)</h2>
        {schedulesQuery.isLoading ? (
          <p className='text-gray-500 text-sm'>Loading…</p>
        ) : schedulesQuery.isError ? (
          <p className='text-red-600 text-sm'>Could not load schedules.</p>
        ) : (
          <div className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto'>
            <table className='w-full text-sm min-w-[640px]'>
              <thead className='bg-gray-50 text-left text-xs uppercase text-gray-500'>
                <tr>
                  <th className='px-4 py-2'>Beat key</th>
                  <th className='px-4 py-2'>Task</th>
                  <th className='px-4 py-2'>Schedule</th>
                  <th className='px-4 py-2'>Last send (related campaigns)</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {(schedulesQuery.data ?? []).map((row) => (
                  <tr key={row.beat_key} className='hover:bg-gray-50'>
                    <td className='px-4 py-2 font-mono text-xs'>{row.beat_key}</td>
                    <td className='px-4 py-2 font-mono text-xs max-w-[240px] truncate' title={row.task}>
                      {row.task}
                    </td>
                    <td className='px-4 py-2 text-xs text-gray-600 max-w-[280px]'>{row.schedule_human}</td>
                    <td className='px-4 py-2 text-xs text-gray-600'>
                      {row.last_send_at ? new Date(row.last_send_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {campaignId && (
        <CampaignDetailDrawer campaignId={campaignId} catalogEntry={drawerEntry} range={range} />
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/drip-campaigns/')({
  component: AdminDripOverviewPage,
});
